'use client';

import React, { useEffect, useState } from 'react';
import classNames from 'classnames';

import { Button, Input } from '@/components/atoms';
import { Label } from '@/components/atoms/Label';
import { Modal, useModalController } from '@/components/molecules/Modal';

import { useDashboard, usePageActions } from '@/modules';
import { useAuth } from '@/modules/auth';
import { canPublishGlobally } from '@/modules/widgets/utils/permissions';
import { Page } from '@/modules/pages/types';

interface NewWidgetModalProps {
    controller: ReturnType<typeof useModalController>;
    page?: Page;
}


export function NewPageModal({
    controller,
    page
}: NewWidgetModalProps) {
    const [title, setTitle] = useState('');
    const [loading, setLoading] = useState(false);
    const [publishToAll, setPublishToAll] = useState(false);

    const { setPendingNewPage } = usePageActions();
    const { state: { currentDashboard } } = useDashboard();
    const { user } = useAuth();
    const canPublish = canPublishGlobally(user);

    useEffect(() => {
        if (!controller.isOpen) {
            return;
        }

        setTitle(page?.title ?? '');
        setPublishToAll(Boolean((page as any)?.publishedToAll));
    }, [controller.isOpen, page]);

    const handleSubmit = async () => {
        if (!currentDashboard) return;

        // For editing existing pages, keep the old behavior
        if (page?.id) {
            // TODO: keep edit flow if needed
            controller.close();
            return;
        }

        // Store pending page data and show the empty state with options
        setPendingNewPage({
            title,
            publishedToAll: publishToAll,
        });
        setTitle('');
        setPublishToAll(false);
        controller.close();
    };



    return (
        <>
            <Modal
                title={page?.id ? "Edit Report" : "Create New Page"}
                bodyClassName="max-w-md"
                actionButtons={[
                    <Button key="cancel" disabled={loading} variant='light' onClick={controller.close}> Cancel </Button>,
                    <Button key="create" isLoading={loading} variant='secondary' disabled={!title} onClick={handleSubmit}>
                        {page?.id ? "Update Report" : "Create Page"}
                    </Button>
                ]}
                controller={controller}
                closeOnOverlayClick={!loading}
                closeOnEsc={!loading}
            >
                <div className="space-y-4 mb-1.5">
                    <div className="space-y-2">
                        <Label text="Page Name">
                            <Input placeholder="Enter page name..." value={title} onChange={(e) => setTitle(e.target.value)} />
                        </Label>
                    </div>

                    {canPublish && (
                        <div className="flex items-center justify-between pt-2">
                            <div>
                                <h4 className="font-medium text-sm text-[#2d3a3f]">Visible to everybody</h4>
                                <p className="text-xs text-[#5a717a]">
                                    Anyone in your organization can view this page
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setPublishToAll((prev) => !prev)}
                                className={`inline-flex h-6 w-12 items-center rounded-full transition-colors ${publishToAll ? "bg-[#2d3a3f]" : "bg-[#c9d1d4]"}`}
                                aria-pressed={publishToAll}
                            >
                                <span
                                    className={`ml-0.5 inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${publishToAll ? "translate-x-6" : "translate-x-0"}`}
                                />
                            </button>
                        </div>
                    )}
                </div>
            </Modal>
        </>
    );
}
